// @ts-strict-ignore
/**
 * FX HedgePool Inc. Proprietary and Confidential
 *
 * This source code and the algorithms implemented herein are the sole property of FX HedgePool, Inc.,
 * are confidential information, and are trade secrets of FX HedgePool, Inc.
 * Redistribution or use of this source code or the algorithms implemented herein, in whole or in part,
 * without the express written permission of FX HedgePool, Inc. is strictly prohibited.
 */

import { Injectable, OnDestroy } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, Observer, Subscription, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  CreateEntityPayload,
  Entity,
  Parties,
  UpdateEntityPayload,
} from '../models/parties.model';
import {
  User,
  CreateUserPayload,
  UpdateUserPayload,
  ExistingUserValidationPayload,
  ExistingUserValidationResponsePayload,
  DeleteUserPayload,
} from '../models/user';
import { Benchmark } from '../models/rates.model';
import { ActivatedRoute } from '@angular/router';
import { Roles } from '../models/roles.model';
import {
  RollCycleDate,
  NewCurrencyPairPayload,
  Tenor,
} from '../models/roll-dates.model';
import {
  CalendarPoolResponse,
  PoolPositionsReport,
} from '../models/pool.model';
import { EntityRate } from '../components/settings/fee-tab/fee-calculation/models';
import { ValidatePoolPayload, ValidateResponse } from '../models/date.model';
import { DateValidationResponse } from '../models/dateValidateResponse.model';

@Injectable({
  providedIn: 'root',
})
export class AdminService implements OnDestroy {
  /**
   * @summary
   * Parties is the cached list of all members, credit banks, and intermediaries
   * PartiesClearedSubscritpion emits when a value in the parties list is edited or deleted
   * QueryParamsSubscription I'm pretty sure this is duplicated code since it works as well in the party Selector
   * Might still be needed - need to confirm. Removed deprecated tag for now
   */
  private parties: Parties;
  public partiesClearedSubscription = new Subject<void>();
  private queryParamsSubscription: Subscription;

  constructor(
    private readonly api: ApiService,
    private readonly activeRoute: ActivatedRoute,
  ) {}

  public setHeaderForAdmin(): Observable<number> {
    return new Observable((observer: Observer<number>) => {
      this.queryParamsSubscription = this.activeRoute.queryParams.subscribe(
        (params) => {
          if (params['id'] && params['partyType']) {
            const memberId = parseInt(params['id'], 10);
            const partyType = params['partyType'];
            if (!isNaN(memberId)) {
              this.api.setAdminAsMemberHeaders(memberId, partyType);
              observer.next(memberId);
            }
          }
        },
      );
    });
  }

  public ngOnDestroy(): void {
    if (this.queryParamsSubscription) {
      this.queryParamsSubscription.unsubscribe();
    }
    if (this.partiesClearedSubscription) {
      this.partiesClearedSubscription.unsubscribe();
    }
  }

  public getUsers(entityId: string): Observable<User[]> {
    return this.api.get(`v1/user/entity?id=${entityId}`, {}, environment.zuul);
  }

  public getRoles(): Observable<Roles[]> {
    return this.api.get(`v1/user/roles`, {}, environment.zuul);
  }

  public updateLastLoginTime(): Observable<void> {
    return this.api.put(`v1/user/lastLogin`, {});
  }

  public activateUser(): Observable<void> {
    return this.api.put(`v1/user/activate`, {});
  }

  public getParties(): Observable<Parties> {
    if (this.parties) {
      return new Observable((observer: Observer<Parties>) => {
        observer.next(this.parties);
        observer.complete();
      });
    }
    return this.api.get(`v1/user/entity-all`, {}, environment.zuul);
  }

  public setParties(parties: Parties): void {
    this.parties = parties;
  }

  public clearParties(): void {
    this.parties = null;
    this.partiesClearedSubscription.next();
  }

  public createEntity(
    createEntityPayload: CreateEntityPayload,
  ): Observable<void> {
    return this.api.post(
      'v1/user/entity',
      createEntityPayload,
      {},
      environment.zuul,
    );
  }

  public async createEntityAsync(
    createEntityPayload: CreateEntityPayload,
  ): Promise<Entity> {
    return await this.api.postAwait(
      'v1/user/entity',
      createEntityPayload,
      {},
      environment.zuul,
    );
  }

  public deleteEntity(id: string): Observable<void> {
    return this.api.deleteApi(`v1/user/entity?id=${id}`, environment.zuul);
  }

  public deletePools(poolArr: string[]): Observable<void> {
    return this.api.deleteApi(
      `admin/pools`,
      environment.backendServer,
      poolArr,
    );
  }

  public updateEntity(
    updateEntityPayload: UpdateEntityPayload,
  ): Observable<void> {
    return this.api.put(
      'v1/user/entity',
      updateEntityPayload,
      {},
      environment.zuul,
    );
  }

  public createUser(createUserPayload: CreateUserPayload): Observable<void> {
    return this.api.post('v1/user', createUserPayload, {}, environment.zuul);
  }

  public validateUserCreation(
    validationPayload: ExistingUserValidationPayload,
  ): Observable<ExistingUserValidationResponsePayload> {
    return this.api.post(
      `v1/user/validate-creation`,
      validationPayload,
      {},
      environment.zuul,
    );
  }

  public updateUserDetails(params: UpdateUserPayload): Observable<void> {
    return this.api.put(`v1/user`, params, {}, environment.zuul);
  }

  public fetchFxhpDates(
    dateFilter: Date,
    identifier: string,
  ): Observable<CalendarPoolResponse[]> {
    return this.api.get(
      `date/all?startDate=${dateFilter.toISOString().substring(0, 10)}&identifier=${identifier}`,
      {},
      environment.backendServer,
    );
  }

  public fetchAllTenors(): Observable<Tenor[]> {
    return this.api.get(
      `date/all-tenors?active=true`,
      {},
      environment.backendServer,
    );
  }

  public fetchAllBenchmarkss(): Observable<Benchmark[]> {
    return this.api.get(
      `date/all-benchmarks?active=true`,
      {},
      environment.backendServer,
    );
  }

  public updateFxhpDates(
    rollCycleDates: RollCycleDate[],
    identifier: string,
  ): Observable<CalendarPoolResponse[]> {
    return this.api.put(
      `date?identifier=${identifier}`,
      rollCycleDates,
      {},
      environment.backendServer,
    );
  }

  public validateNewCurrencyPair(
    currencyPair: string,
    tenor: string,
    benchmark: string,
  ): Observable<boolean> {
    return this.api.post(
      `admin/validate/new-pool`,
      {
        currencyPair: currencyPair,
        poolIdentifier: tenor,
        benchmarkIdentifier: benchmark,
      },
      {},
      environment.backendServer,
    );
  }

  public async requestDateRangeForNewCurrencyPairOnBoarding(
    tenor: string,
    currencyPair: string,
    validate: boolean,
  ): Promise<DateValidationResponse> {
    return await this.api.getAwait(
      `admin/new-pool/dates?tenor=${tenor}&ccy=${currencyPair}&validate=${validate}`,
      {},
      environment.backendServer,
    );
  }

  public async coppClarkValidate(
    payload: ValidatePoolPayload,
  ): Promise<ValidateResponse> {
    return await this.api.postAwait(
      `admin/validate/new-pool`,
      payload,
      {},
      environment.backendServer,
    );
  }

  public async createNewCurrencyPair(
    newCurrencyPairPayload: NewCurrencyPairPayload,
  ): Promise<void> {
    return await this.api.postAwait(
      `admin/new-pool`,
      newCurrencyPairPayload,
      {},
      environment.backendServer,
    );
  }

  public deactivateUser(userId: string): Observable<void> {
    return this.api.put(
      `v1/user/deactivate?id=${userId}`,
      {},
      {},
      environment.zuul,
    );
  }

  public resendActivationCode(userId: string): Observable<void> {
    return this.api.post(
      `v1/user/resend-activation-email?id=${userId}`,
      {},
      {},
      environment.zuul,
    );
  }

  public unlockUser(userId: string): Observable<void> {
    return this.api.put(
      `v1/user/unlock?id=${userId}`,
      {},
      {},
      environment.zuul,
    );
  }

  public reactivateUser(userId: string): Observable<void> {
    return this.api.put(
      `v1/user/reactivate?id=${userId}`,
      {},
      {},
      environment.zuul,
    );
  }

  public deleteUser(deleteUserPayload: DeleteUserPayload): Observable<void> {
    return this.api.deleteApi(`v1/user`, environment.zuul, deleteUserPayload);
  }

  public findUserById(userId: string): Observable<User> {
    return this.api.get(`v1/user?id=${userId}`, {}, environment.zuul);
  }

  public memberRates(memberId: number): Observable<EntityRate[]> {
    return this.api.get(
      `rate/member?entityId=${memberId}`,
      {},
      environment.backendServer,
    );
  }

  public poolReport(tenor: string): Observable<PoolPositionsReport[]> {
    return this.api.get(
      `admin/latest-pool-positions?tenor=${tenor}`,
      {},
      environment.backendServer,
    );
  }
}
